import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Brand } from './components/Brand'
import { Artwork } from './components/Artwork'
import { Icon } from './components/Icon'
import { ProductCard } from './components/ProductCard'
import { catalogOrigin, commerceApiOrigin, storefrontFaultCopy } from './config'
import { loadPublishedCatalog, sampleCatalog } from './data/catalog'
import { defaultStorefrontContent, loadStorefrontContent, type StorefrontContent } from './data/storefront-content'
import type { CartLine, CatalogProduct } from './types'

type Route =
  | { page: 'home' }
  | { page: 'catalog' }
  | { page: 'product'; slug: string }
  | { page: 'cart' }
  | { page: 'checkout' }
  | { page: 'legal' }
  | { page: 'returns' }

type CheckoutStatus =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'notice'; message: string }
  | { state: 'error'; message: string }

type Theme = 'light' | 'dark'

const cartStorageKey = 'mudaii-cart-v1'
const themeStorageKey = 'mudaii-theme-v1'
const categoryIcons = ['info', 'bag', 'heart', 'search'] as const

function safeCart(): CartLine[] {
  try {
    const stored = window.localStorage.getItem(cartStorageKey)
    if (!stored) return []
    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((line): line is CartLine => {
        if (!line || typeof line !== 'object') return false
        const candidate = line as Record<string, unknown>
        return typeof candidate.variantId === 'string' && Number.isInteger(candidate.quantity) && Number(candidate.quantity) > 0
      })
      .map((line) => ({ variantId: line.variantId, quantity: Math.min(line.quantity, 99) }))
  } catch {
    return []
  }
}

function safeTheme(): Theme {
  try {
    return window.localStorage.getItem(themeStorageKey) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

function routeFromLocation(): Route {
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/catalog') return { page: 'catalog' }
  if (path === '/cart') return { page: 'cart' }
  if (path === '/checkout') return { page: 'checkout' }
  if (path === '/legal') return { page: 'legal' }
  if (path === '/returns') return { page: 'returns' }
  if (path.startsWith('/products/')) {
    const slug = decodeURIComponent(path.slice('/products/'.length))
    return { page: 'product', slug }
  }
  return { page: 'home' }
}

function pathForRoute(route: Route): string {
  if (route.page === 'product') return '/products/' + encodeURIComponent(route.slug)
  if (route.page === 'catalog') return '/catalog'
  if (route.page === 'cart') return '/cart'
  if (route.page === 'checkout') return '/checkout'
  if (route.page === 'legal') return '/legal'
  if (route.page === 'returns') return '/returns'
  return '/'
}

function yen(value: number): string {
  return '￥' + value.toLocaleString('ja-JP')
}

export function App() {
  const [route, setRoute] = useState<Route>(routeFromLocation)
  const [cart, setCart] = useState<CartLine[]>(safeCart)
  const [catalog, setCatalog] = useState<CatalogProduct[]>(catalogOrigin ? [] : sampleCatalog)
  const [catalogError, setCatalogError] = useState(false)
  const [content, setContent] = useState<StorefrontContent>(defaultStorefrontContent)
  const [theme, setTheme] = useState<Theme>(safeTheme)

  useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    window.localStorage.setItem(cartStorageKey, JSON.stringify(cart))
  }, [cart])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    window.localStorage.setItem(themeStorageKey, theme)
  }, [theme])

  useEffect(() => {
    let cancelled = false
    void loadPublishedCatalog()
      .then((products) => {
        if (!cancelled) setCatalog(products)
      })
      .catch(() => {
        if (!cancelled) setCatalogError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void loadStorefrontContent().then((nextContent) => {
      if (!cancelled) setContent(nextContent)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const cartCount = cart.reduce((sum, line) => sum + line.quantity, 0)

  const navigate = (next: Route) => {
    window.history.pushState({}, '', pathForRoute(next))
    setRoute(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const addToCart = (variantId: string) => {
    setCart((current) => {
      const existing = current.find((line) => line.variantId === variantId)
      if (!existing) return [...current, { variantId, quantity: 1 }]
      return current.map((line) =>
        line.variantId === variantId
          ? { ...line, quantity: Math.min(line.quantity + 1, 99) }
          : line
      )
    })
  }

  const updateQuantity = (variantId: string, quantity: number) => {
    setCart((current) =>
      current
        .map((line) => (line.variantId === variantId ? { ...line, quantity } : line))
        .filter((line) => line.quantity > 0)
    )
  }

  return (
    <main className="site-backdrop" data-theme={theme}>
      <div className="mobile-shell">
        {route.page === 'home' && (
          <HomePage
            catalog={catalog}
            catalogError={catalogError}
            content={content}
            cartCount={cartCount}
            onAdd={addToCart}
            onNavigate={navigate}
            onToggleTheme={() => setTheme((current) => current === 'light' ? 'dark' : 'light')}
            theme={theme}
          />
        )}
        {route.page === 'catalog' && (
          <CatalogPage catalog={catalog} catalogError={catalogError} onAdd={addToCart} onNavigate={navigate} />
        )}
        {route.page === 'product' && (
          <ProductPage
            catalog={catalog}
            cartCount={cartCount}
            onAdd={addToCart}
            onNavigate={navigate}
            product={catalog.find((product) => product.slug === route.slug)}
          />
        )}
        {route.page === 'cart' && (
          <CartPage
            cart={cart}
            catalog={catalog}
            onNavigate={navigate}
            onUpdateQuantity={updateQuantity}
          />
        )}
        {route.page === 'checkout' && <CheckoutPage cart={cart} catalog={catalog} onNavigate={navigate} />}
        {route.page === 'legal' && <LegalPage onNavigate={navigate} />}
        {route.page === 'returns' && <ReturnsPage onNavigate={navigate} />}
        <BottomNav cartCount={cartCount} currentPage={route.page} onNavigate={navigate} />
      </div>
    </main>
  )
}

function TopBar({
  back,
  cartCount,
  home = false,
  onNavigate,
  onToggleTheme,
  theme,
  title,
  showFavorite = false,
  favorite = false,
  onToggleFavorite,
  showCart = true
}: {
  back?: Route
  cartCount?: number
  home?: boolean
  onNavigate: (route: Route) => void
  onToggleTheme?: () => void
  theme?: Theme
  title?: string
  showFavorite?: boolean
  favorite?: boolean
  onToggleFavorite?: () => void
  showCart?: boolean
}) {
  if (home) {
    const nextTheme = theme === 'dark' ? '浅色' : '深色'
    return (
      <header className="top-bar top-bar--home">
        <button
          aria-label={'切换为' + nextTheme + '模式'}
          className="icon-button theme-toggle"
          onClick={onToggleTheme}
          type="button"
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={23} />
        </button>
        <Brand />
        <button
          aria-label="商品を探す"
          className="icon-button top-bar__menu"
          onClick={() => onNavigate({ page: 'catalog' })}
          type="button"
        >
          <Icon name="menu" size={30} stroke={1.45} />
        </button>
      </header>
    )
  }

  return (
    <header className="top-bar">
      <button
        aria-label={back ? '前の画面へ戻る' : '商品を探す'}
        className="icon-button"
        onClick={() => onNavigate(back ?? { page: 'catalog' })}
        type="button"
      >
        <Icon name={back ? 'back' : 'menu'} />
      </button>
      {title ? <span className="top-bar__title">{title}</span> : <Brand />}
      <span className="top-bar__actions">
        {showFavorite ? <button aria-label={favorite ? 'お気に入りから外す' : 'お気に入りに追加'} className={favorite ? 'icon-button favorite-button favorite-button--active' : 'icon-button favorite-button'} onClick={onToggleFavorite} type="button"><Icon name="heart" /></button> : null}
        {showCart ? <button
          aria-label="カートを開く"
          className="icon-button icon-button--bag"
          onClick={() => onNavigate({ page: 'cart' })}
          type="button"
        >
          <Icon name="bag" />
          {cartCount ? <span className="cart-count">{cartCount}</span> : null}
        </button> : <span aria-hidden="true" className="top-bar__action-placeholder" />}
      </span>
    </header>
  )
}

function HomePage({
  catalog,
  catalogError,
  content,
  cartCount,
  onAdd,
  onNavigate,
  onToggleTheme,
  theme
}: {
  catalog: CatalogProduct[]
  catalogError: boolean
  content: StorefrontContent
  cartCount: number
  onAdd: (variantId: string) => void
  onNavigate: (route: Route) => void
  onToggleTheme: () => void
  theme: Theme
}) {
  const heroImageUrl = theme === 'dark' ? content.hero.darkImageUrl : content.hero.imageUrl

  return (
    <>
      <TopBar home cartCount={cartCount} onNavigate={onNavigate} onToggleTheme={onToggleTheme} theme={theme} />
      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">{content.hero.eyebrow}</p>
          <h1><span>{content.hero.titleLine1}</span><span>{content.hero.titleLine2}</span></h1>
          <p>{content.hero.description}</p>
        </div>
        <div className="hero__art" role="img" aria-label={content.hero.imageAlt}>
          <img className={theme === 'dark' ? 'hero__image--dark' : undefined} src={heroImageUrl} alt={content.hero.imageAlt} />
        </div>
      </section>

      <section className="category-row" aria-label="商品カテゴリ">
        {content.categories.map((category, index) => (
          <button className="category-pill" key={category.id || category.slug || category.name} onClick={() => onNavigate({ page: 'catalog' })} type="button">
            <span className="category-pill__mark"><Icon name={categoryIcons[index % categoryIcons.length]} size={20} /></span>
            {category.name}
          </button>
        ))}
      </section>

      <section className="content-section">
        <div className="section-heading">
          <h2>{content.featured.title}</h2>
          <button className="text-link" onClick={() => onNavigate({ page: 'catalog' })} type="button">
            {content.featured.linkLabel} <Icon name="arrow" size={17} />
          </button>
        </div>
        <div className="product-rail">
          {catalog.map((product) => (
            <ProductCard
              key={product.id}
              onAdd={onAdd}
              onSelect={(slug) => onNavigate({ page: 'product', slug })}
              product={product}
            />
          ))}
        </div>
        {catalogError ? <p className="catalog-fault">{storefrontFaultCopy}</p> : null}
      </section>

      <section className="story-card">
        <div>
          <p className="eyebrow">{content.story.eyebrow}</p>
          <h2>{content.story.title}</h2>
          <p>{content.story.description.split(/\r?\n/u).map((line, index) => <span key={`${index}-${line}`}>{index > 0 ? <br /> : null}{line}</span>)}</p>
          <button className="dark-button dark-button--small" onClick={() => onNavigate({ page: 'catalog' })} type="button">
            {content.story.buttonLabel} <Icon name="arrow" size={18} />
          </button>
        </div>
        <div className="story-card__art" role="img" aria-label={content.story.imageAlt}>
          <img src={content.story.imageUrl} alt={content.story.imageAlt} />
        </div>
      </section>
      <nav className="site-links" aria-label="ご案内">
        <button type="button" onClick={() => onNavigate({ page: 'legal' })}>{content.legalLinkLabel}</button>
        <button type="button" onClick={() => onNavigate({ page: 'returns' })}>{content.returnsLinkLabel}</button>
      </nav>
    </>
  )
}

function CatalogPage({
  catalog,
  catalogError,
  onAdd,
  onNavigate
}: {
  catalog: CatalogProduct[]
  catalogError: boolean
  onAdd: (variantId: string) => void
  onNavigate: (route: Route) => void
}) {
  return (
    <>
      <TopBar back={{ page: 'home' }} onNavigate={onNavigate} title="見つける" />
      <section className="page-heading">
        <p className="eyebrow">OBJECT COLLECTION</p>
        <h1>気になるものを、<br />ゆっくり探す。</h1>
      </section>
      <div className="filter-row" aria-label="カテゴリフィルター">
        <button className="filter-pill filter-pill--active" type="button">すべて</button>
        <button className="filter-pill" type="button">一点もの</button>
        <button className="filter-pill" type="button">予約販売</button>
      </div>
      <section className="catalog-stack">
        {catalog.map((product) => (
          <ProductCard
            key={product.id}
            onAdd={onAdd}
            onSelect={(slug) => onNavigate({ page: 'product', slug })}
            product={product}
          />
        ))}
      </section>
      {catalog.length === 0 ? <p className="catalog-fault">{catalogError ? storefrontFaultCopy : 'カタログを読み込んでいます。'}</p> : null}
    </>
  )
}

function ProductPage({
  catalog,
  cartCount,
  onAdd,
  onNavigate,
  product
}: {
  catalog: CatalogProduct[]
  cartCount: number
  onAdd: (variantId: string) => void
  onNavigate: (route: Route) => void
  product?: CatalogProduct
}) {
  const [quantity, setQuantity] = useState(1)
  const [favorite, setFavorite] = useState(false)

  if (!product) {
    return (
      <>
        <TopBar back={{ page: 'catalog' }} cartCount={cartCount} onNavigate={onNavigate} title="商品が見つかりません" />
        <section className="empty-state">
          <h1>この商品は見つかりませんでした。</h1>
          <button className="dark-button" onClick={() => onNavigate({ page: 'catalog' })} type="button">商品一覧へ</button>
        </section>
      </>
    )
  }

  return (
    <>
      <TopBar back={{ page: 'catalog' }} cartCount={cartCount} favorite={favorite} onNavigate={onNavigate} onToggleFavorite={() => setFavorite((current) => !current)} showFavorite />
      <section className="product-detail">
        <div className="product-detail__visual">
          <Artwork className="product-detail__art" product={product} />
          <span className="art-count">1 / {Math.max(product.gallery.length, 1)}</span>
        </div>
        <div className="art-dots" aria-label={'商品画像 1 / ' + Math.max(product.gallery.length, 1)}>
          {product.gallery.slice(0, 5).map((_, index) => <span className={index === 0 ? 'active' : ''} key={index} />)}
        </div>
        <div className="product-detail__copy">
          <h1>{product.name}</h1>
          <p className="product-detail__price">{yen(product.price)}</p>
          <p className="product-detail__lead">{product.shortDescription}</p>
          <div className="badge-row">
            {product.badges.map((badge) => <span className="badge" key={badge}>{badge}</span>)}
          </div>
          <p className="product-detail__description">{product.description}</p>
          <p className="product-spec">素材：{product.material}<br />サイズ：{product.dimensions}</p>
        </div>
        <div className="purchase-row">
          <div className="quantity-control" aria-label="数量">
            <button aria-label="数量を減らす" disabled={quantity <= 1} onClick={() => setQuantity(quantity - 1)} type="button"><Icon name="minus" size={19} /></button>
            <span>{quantity}</span>
            <button aria-label="数量を増やす" disabled={quantity >= 99} onClick={() => setQuantity(quantity + 1)} type="button"><Icon name="plus" size={19} /></button>
          </div>
          <button
            className="dark-button purchase-row__button"
            onClick={() => {
              for (let index = 0; index < quantity; index += 1) onAdd(product.variantId)
              onNavigate({ page: 'cart' })
            }}
            type="button"
          >
            <Icon name="bag" size={22} /> カートに入れる
          </button>
        </div>
        <details className="detail-disclosure">
          <summary><Icon name="truck" size={21} /> 配送について <Icon name="arrow" size={18} /></summary>
          <p>日本国内は全国送料無料です。支払い確認後、48時間以内の発送を目標とします。</p>
        </details>
        <details className="detail-disclosure">
          <summary><Icon name="info" size={21} /> ご注意 <Icon name="arrow" size={18} /></summary>
          <p>掲載中の内容はダミー商品情報です。購入手続き時にはサーバーが商品状態、価格、配送費、在庫を再確認します。</p>
        </details>
        <section className="related-section">
          <h2>いっしょに置きたいもの</h2>
          <div className="related-rail">
            {catalog.filter((candidate) => candidate.id !== product.id).slice(0, 3).map((candidate) => (
              <ProductCard key={candidate.id} product={candidate} onAdd={onAdd} onSelect={(slug) => onNavigate({ page: 'product', slug })} />
            ))}
          </div>
        </section>
      </section>
    </>
  )
}

function CartPage({
  cart,
  catalog,
  onNavigate,
  onUpdateQuantity
}: {
  cart: CartLine[]
  catalog: CatalogProduct[]
  onNavigate: (route: Route) => void
  onUpdateQuantity: (variantId: string, quantity: number) => void
}) {
  const lines = useMemo(
    () =>
      cart.flatMap((line) => {
        const product = catalog.find((candidate) => candidate.variantId === line.variantId)
        return product ? [{ ...line, product }] : []
      }),
    [cart]
  )
  const subtotal = lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0)

  return (
    <>
      <TopBar back={{ page: 'home' }} onNavigate={onNavigate} title="CART" />
      <section className="cart-page">
        <Brand />
        {lines.length === 0 ? (
          <div className="empty-state">
            <p>カートはまだ空です。</p>
            <button className="dark-button" onClick={() => onNavigate({ page: 'catalog' })} type="button">見つけにいく</button>
          </div>
        ) : (
          <>
            <div className="cart-lines">
              {lines.map((line) => (
                <article className="cart-line" key={line.variantId}>
                  <Artwork className="cart-line__art" product={line.product} />
                  <div className="cart-line__copy">
                    <h2>{line.product.name}</h2>
                    <p>{yen(line.product.price)}</p>
                    <div className="quantity-control quantity-control--small">
                      <button aria-label={line.product.name + ' を減らす'} onClick={() => onUpdateQuantity(line.variantId, line.quantity - 1)} type="button"><Icon name="minus" size={17} /></button>
                      <span>{line.quantity}</span>
                      <button aria-label={line.product.name + ' を増やす'} onClick={() => onUpdateQuantity(line.variantId, line.quantity + 1)} type="button"><Icon name="plus" size={17} /></button>
                    </div>
                  </div>
                  <button aria-label={line.product.name + ' をカートから外す'} className="remove-button" onClick={() => onUpdateQuantity(line.variantId, 0)} type="button"><Icon name="close" size={20} /></button>
                </article>
              ))}
            </div>
            <div className="shipping-note"><Icon name="truck" size={31} /><div><strong>送料　￥0</strong><span>日本国内は全国送料無料</span></div></div>
            <div className="coupon-box"><Icon name="ticket" size={21} /><span>クーポンコード（ダミー）</span><button type="button">適用する</button></div>
            <OrderSummary subtotal={subtotal} />
            <button className="dark-button checkout-button" onClick={() => onNavigate({ page: 'checkout' })} type="button">ご購入手続きへ <Icon name="arrow" /></button>
            <button className="underlined-button" onClick={() => onNavigate({ page: 'catalog' })} type="button">買い物を続ける</button>
          </>
        )}
      </section>
    </>
  )
}

function OrderSummary({ subtotal }: { subtotal: number }) {
  return (
    <div className="order-summary">
      <div><span>小計</span><span>{yen(subtotal)}</span></div>
      <div><span>送料</span><span>￥0</span></div>
      <div className="order-summary__total"><strong>合計</strong><strong>{yen(subtotal)}</strong></div>
    </div>
  )
}

function CheckoutPage({
  cart,
  catalog,
  onNavigate
}: {
  cart: CartLine[]
  catalog: CatalogProduct[]
  onNavigate: (route: Route) => void
}) {
  const [status, setStatus] = useState<CheckoutStatus>({ state: 'idle' })
  const lines = useMemo(
    () =>
      cart.flatMap((line) => {
        const product = catalog.find((candidate) => candidate.variantId === line.variantId)
        return product ? [{ ...line, product }] : []
      }),
    [cart]
  )
  const subtotal = lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0)

  const submitCheckout = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (lines.length === 0) {
      setStatus({ state: 'error', message: 'カートに商品を追加してから、もう一度お試しください。' })
      return
    }
    if (!commerceApiOrigin) {
      setStatus({ state: 'notice', message: storefrontFaultCopy })
      return
    }

    const form = new FormData(event.currentTarget)
    const idempotencyKey = crypto.randomUUID()
    setStatus({ state: 'sending' })

    try {
      const response = await fetch(commerceApiOrigin + '/v1/checkout', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'idempotency-key': idempotencyKey
        },
        body: JSON.stringify({
          items: lines.map((line) => ({ variant_id: line.variantId, quantity: line.quantity })),
          customer: {
            name: String(form.get('name') || ''),
            email: String(form.get('email') || ''),
            phone: String(form.get('phone') || '')
          },
          shipping_address: {
            postal_code: String(form.get('postal_code') || ''),
            prefecture: String(form.get('prefecture') || ''),
            city: String(form.get('city') || ''),
            address1: String(form.get('address1') || '')
          }
        })
      })
      const payload: unknown = await response.json().catch(() => null)
      if (!response.ok || !payload || typeof payload !== 'object') {
        throw new Error('checkout-request-failed')
      }
      const record = payload as Record<string, unknown>
      if (typeof record.payment_url !== 'string' || typeof record.reservation_expires_at !== 'string') {
        throw new Error('checkout-response-incomplete')
      }
      setStatus({
        state: 'notice',
        message: '商品を一時的に確保しました。お支払い画面へ移動します。'
      })
      window.location.assign(record.payment_url)
    } catch {
      setStatus({
        state: 'error',
        message: '現在カートへの追加を完了できません。商品は確保されていません。時間をおいて再度お試しください。'
      })
    }
  }

  return (
    <>
      <TopBar back={{ page: 'cart' }} onNavigate={onNavigate} showCart={false} />
      <section className="checkout-page">
        <p className="checkout-page__title">CHECKOUT</p>
        <div className="checkout-progress" aria-label="購入手続きの進行状況">
          <span className="active">1<br />お届け先</span><i /><span>2<br />お支払い</span><i /><span>3<br />確認</span>
        </div>
        {lines.length === 0 ? (
          <div className="empty-state">
            <p>カートが空です。</p>
            <button className="dark-button" onClick={() => onNavigate({ page: 'catalog' })} type="button">商品を見る</button>
          </div>
        ) : (
          <form onSubmit={submitCheckout}>
            <fieldset className="checkout-section">
              <legend>1. お届け先</legend>
              <label><span>お名前</span><input autoComplete="name" name="name" placeholder="お名前" required /></label>
              <div className="field-pair">
                <label><span>郵便番号</span><input autoComplete="postal-code" inputMode="numeric" name="postal_code" placeholder="郵便番号" required /></label>
                <label><span>都道府県</span><input autoComplete="address-level1" name="prefecture" placeholder="都道府県" required /></label>
              </div>
              <label><span>市区町村</span><input autoComplete="address-level2" name="city" placeholder="市区町村" required /></label>
              <label><span>番地・建物名</span><input autoComplete="street-address" name="address1" placeholder="番地・建物名" required /></label>
              <label><span>電話番号</span><input autoComplete="tel" inputMode="tel" name="phone" placeholder="電話番号" required /></label>
              <label><span>メールアドレス</span><input autoComplete="email" name="email" placeholder="メールアドレス" required type="email" /></label>
            </fieldset>
            <fieldset className="checkout-section">
              <legend>2. 配送方法</legend>
              <p className="payment-choice"><Icon name="truck" size={21} /> 通常配送 / 全国送料無料</p>
            </fieldset>
            <fieldset className="checkout-section">
              <legend>3. お支払い方法</legend>
              <p className="checkout-help">お支払いはKOMOJUの安全な決済画面で確認・選択します。利用可能な決済方法はKOMOJU側の表示をご確認ください。</p>
            </fieldset>
            <section className="checkout-order">
              <h2>4. ご注文内容</h2>
              {lines.map((line) => <div key={line.variantId}><span>{line.product.name} × {line.quantity}</span><span>{yen(line.product.price * line.quantity)}</span></div>)}
              <OrderSummary subtotal={subtotal} />
            </section>
            {status.state !== 'idle' && (
              <p className={'checkout-status checkout-status--' + status.state} role="status">
                {status.state === 'sending' ? 'ご注文内容を安全に確認しています。' : status.message}
              </p>
            )}
            <button className="dark-button checkout-button" disabled={status.state === 'sending'} type="submit">
              {status.state === 'sending' ? '確認中…' : '注文を確定する'} <Icon name="arrow" />
            </button>
          </form>
        )}
      </section>
    </>
  )
}

function ReturnsPage({ onNavigate }: { onNavigate: (route: Route) => void }) {
  return (
    <>
      <TopBar back={{ page: 'home' }} onNavigate={onNavigate} />
      <article className="legal-page">
        <h1>キャンセル・返品・返金</h1>
        <p>商品到着後7日以内にメールでご連絡ください。返送前に、必ず当店からの案内をご確認ください。</p>
        <h2>お客様都合による返品</h2>
        <p>返送料はお客様のご負担です。未使用・未開封の商品は、状態確認後に返金します。</p>
        <h2>開封済み・使用済みの商品</h2>
        <p>開封済み・使用済みの商品は、確認のうえ残存価値をもとに対応します。</p>
        <h2>破損・不良・誤配送</h2>
        <p>当店理由による破損・不良・誤配送の場合は、返送料を当店が負担します。状態が分かる写真を添えてご連絡ください。</p>
      </article>
    </>
  )
}

function LegalPage({ onNavigate }: { onNavigate: (route: Route) => void }) {
  const rows = [
    ['販売事業者', 'HI-TOPPER合同会社'],
    ['運営責任者', '趙彦淇'],
    ['所在地', '〒584-0056 大阪府富田林市嬉283-82'],
    ['電話番号', '080-5917-1544'],
    ['メールアドレス', 'siolyjow@gmail.com'],
    ['販売価格', '各商品ページに表示'],
    ['送料', '日本国内・全国通常配送無料'],
    ['支払方法', 'KOMOJUの決済画面で表示'],
    ['支払時期', 'KOMOJUの決済画面で表示'],
    ['商品の引渡時期', '支払い確認後48時間以内に発送'],
    ['返品・交換', '到着後7日以内に要連絡']
  ]
  return (
    <>
      <TopBar back={{ page: 'home' }} onNavigate={onNavigate} />
      <article className="legal-page legal-page--table">
        <h1>特定商取引法に基づく表記</h1>
        <div className="legal-table">
          {rows.map(([label, value]) => <div className="legal-table__row" key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </div>
        <a className="contact-card" href="mailto:siolyjow@gmail.com"><span className="contact-card__mark">○</span><span><strong>お問い合わせについて</strong><small>商品やご注文に関するお問い合わせはメールで承ります。</small></span><Icon name="arrow" size={18} /></a>
      </article>
    </>
  )
}

function BottomNav({
  cartCount,
  currentPage,
  onNavigate
}: {
  cartCount: number
  currentPage: Route['page']
  onNavigate: (route: Route) => void
}) {
  const navItems: Array<{ label: string; icon: 'home' | 'search' | 'heart' | 'bag'; route: Route; active: boolean }> = [
    { label: 'HOME', icon: 'home', route: { page: 'home' }, active: currentPage === 'home' },
    { label: '探す', icon: 'search', route: { page: 'catalog' }, active: currentPage === 'catalog' || currentPage === 'product' },
    { label: '', icon: 'heart', route: { page: 'catalog' }, active: false },
    { label: 'CART', icon: 'bag', route: { page: 'cart' }, active: currentPage === 'cart' || currentPage === 'checkout' }
  ]

  return (
    <nav className="bottom-nav" aria-label="メインナビゲーション">
      {navItems.map((item) => (
        <button
          className={item.active ? 'bottom-nav__item bottom-nav__item--active' : 'bottom-nav__item'}
          aria-label={item.label || 'お気に入り'}
          key={item.icon}
          onClick={() => onNavigate(item.route)}
          type="button"
        >
          <span className="bottom-nav__icon"><Icon name={item.icon} size={26} />{item.icon === 'bag' && cartCount ? <i>{cartCount}</i> : null}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  )
}
